/**
 * WelcomeModal — first-login onboarding gate. Welcomes the player and offers
 * two paths:
 *   • "LET'S ROLL"  → emit 'start' (runs the guided tutorial)
 *   • "skip"        → emit 'skip'  (server grants the full reward immediately)
 *
 * Both paths end with the identical reward (100M Stacks + 2 starter dice), so
 * skipping is a legitimate fast-path for experienced players, not a penalty.
 * Body-level singleton, shown from main.ts's auth:success gate when
 * `player.tutorial.completed === false`.
 */

import { EventEmitter } from '../../engine/core/EventEmitter';

interface WelcomeModalEvents {
  /** "LET'S ROLL" tapped — begin the guided tutorial. */
  'start': void;
  /** "skip" confirmed — grant the full reward without the walkthrough. */
  'skip': void;
}

const STYLE_ID = 'sdl-welcome-styles';

export class WelcomeModal extends EventEmitter<WelcomeModalEvents> {
  public element: HTMLElement;
  private visible = false;

  constructor() {
    super();
    WelcomeModal.injectStyles();

    this.element = document.createElement('div');
    this.element.className = 'sdl-welcome-host';
    this.element.innerHTML = `
      <div class="sdl-welcome-backdrop"></div>
      <div class="sdl-welcome-panel">
        <div class="sdl-welcome-kicker">WELCOME TO</div>
        <img class="sdl-welcome-logo" src="/assets/images/logo.png" alt="Street Dice Legends" />
        <div class="sdl-welcome-sub">
          Learn to shoot and work the wallet in two quick rounds.
          Finish up and bag your first <b>2 dice</b> to start your collection.
        </div>
        <button class="sdl-welcome-start" data-start data-sound="button">LET'S ROLL</button>
        <button class="sdl-welcome-skip" data-skip>I already run these streets... skip</button>
        <div class="sdl-welcome-skipnote">You still keep the 2 dice.</div>
      </div>
    `;
    document.body.appendChild(this.element);

    this.element.querySelector('[data-start]')!.addEventListener('click', () => {
      this.emit('start', undefined);
      this.hide();
    });
    this.element.querySelector('[data-skip]')!.addEventListener('click', () => {
      this.emit('skip', undefined);
      this.hide();
    });
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.element.classList.add('visible');
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.remove('visible');
  }

  isVisible(): boolean {
    return this.visible;
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-welcome-host {
        position: fixed; inset: 0;
        z-index: 800;
        display: none;
        align-items: center; justify-content: center;
      }
      .sdl-welcome-host.visible { display: flex; }
      .sdl-welcome-backdrop {
        position: absolute; inset: 0;
        background: rgba(0, 0, 0, 0.8);
        backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
      }
      .sdl-welcome-panel {
        position: relative;
        width: 300px;
        padding: 30px 24px 20px;
        background: var(--concrete, #111114);
        border: 1px solid var(--glass-border, rgba(255,255,255,0.08));
        border-radius: 22px;
        display: flex; flex-direction: column; align-items: center;
        gap: 10px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.65);
        animation: sdl-welcome-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
      }
      @keyframes sdl-welcome-in {
        0% { opacity: 0; transform: translateY(-40px) scale(0.7); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
      }
      /* Readability floor for this dark panel: secondary text never below
         ~0.72 white alpha, tertiary never below ~0.6 — Rajdhani is thin and
         condensed, and lower alphas at these sizes are unreadable on phones
         (the old 0.3 skipnote measured ~2:1 contrast). Hierarchy comes from
         size + weight, not from dimming. */
      .sdl-welcome-kicker {
        font-family: 'Rajdhani', sans-serif;
        font-size: 13px; letter-spacing: 4px;
        font-weight: 600;
        color: rgba(255,255,255,0.72);
      }
      .sdl-welcome-logo {
        width: 230px; max-width: 80%;
        height: auto;
        margin: 2px 0 6px;
        filter: drop-shadow(0 4px 14px rgba(0,0,0,0.5));
        -webkit-user-drag: none; user-select: none;
      }
      .sdl-welcome-sub {
        font-family: 'Rajdhani', sans-serif;
        font-size: 15px; font-weight: 500; line-height: 1.4; text-align: center;
        color: rgba(255,255,255,0.85);
        margin-bottom: 10px;
      }
      .sdl-welcome-sub b { color: #00FF88; font-weight: 700; }
      .sdl-welcome-start {
        width: 100%;
        padding: 15px 0;
        border: none; border-radius: 14px;
        background: linear-gradient(135deg, #FFD75E, #F5A623);
        color: #241a04;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 22px; letter-spacing: 2px;
        cursor: pointer;
        box-shadow: 0 6px 20px rgba(245,166,35,0.3);
        transition: transform 0.1s;
      }
      .sdl-welcome-start:active { transform: scale(0.97); }
      .sdl-welcome-skip {
        background: none; border: none;
        color: rgba(255,255,255,0.78);
        font-family: 'Rajdhani', sans-serif;
        font-size: 14px; font-weight: 500; cursor: pointer;
        padding: 6px 4px 0;
        text-decoration: underline; text-underline-offset: 3px;
      }
      .sdl-welcome-skip:active { color: #ffffff; }
      .sdl-welcome-skipnote {
        font-family: 'Rajdhani', sans-serif;
        font-size: 12px; letter-spacing: 1px; font-weight: 500;
        color: rgba(255,255,255,0.6);
      }
    `;
    document.head.appendChild(style);
  }
}
